/* eslint-disable react-hooks/exhaustive-deps */
import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import OutlinedInput from '@mui/material/OutlinedInput';
import { Box } from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';

import collegeServices from 'services/collegeServices';

/**
 * SPEC_College_Search_Filters (v1.1) — Majors modal (Lovable taxonomy).
 * Type-ahead search + collapsible category tree with per-major multi-select.
 * Selections map to `major_names` (gm_schools.majors.name).
 */

type MajorRow = { major_name: string; category1: string };

const toggle = (list: string[], value: string): string[] =>
  list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

const Chevron = ({ open }: { open: boolean }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

export default function ModalMajor({ open, setOpen, formik }: any) {
  const [result, setResult] = React.useState<MajorRow[]>([]);
  const [keyword, setKeyword] = React.useState('');
  const [selectedMajors, setSelectedMajors] = React.useState<string[]>([]);
  const [expanded, setExpanded] = React.useState<string[]>([]);

  React.useEffect(() => {
    collegeServices
      .getUSCollegeMajorV1({ keyword: '' })
      .then((res: any) => setResult(res ?? []));
  }, []);

  React.useEffect(() => {
    if (!open) return;
    setSelectedMajors(formik.values.major_names ?? []);
  }, [open]);

  const grouped = React.useMemo(() => {
    const map = new Map<string, MajorRow[]>();
    for (const m of result) {
      const cat = m.category1 || 'Other';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(m);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [result]);

  const q = keyword.trim().toLowerCase();
  const searchMatches = q
    ? result.filter((m) => m.major_name.toLowerCase().includes(q))
    : [];

  const MajorRowItem = ({ m }: { m: MajorRow }) => (
    <FormControlLabel
      className="w-100 m-0"
      control={
        <Checkbox
          size="small"
          checked={selectedMajors.includes(m.major_name)}
          onChange={() => setSelectedMajors((p) => toggle(p, m.major_name))}
        />
      }
      label={<span style={{ fontSize: 14 }}>{m.major_name}</span>}
    />
  );

  return (
    <Dialog open={open} onClose={() => setOpen(false)} keepMounted fullWidth maxWidth="sm">
      <DialogTitle className="fw-bold fs-3">Majors</DialogTitle>
      <DialogContent>
        <OutlinedInput
          margin="dense"
          placeholder="Search majors…"
          fullWidth
          size="small"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          sx={{ mb: 1.5 }}
        />

        {q ? (
          <Box sx={{ maxHeight: '50vh', overflow: 'auto' }}>
            {searchMatches.length === 0 && (
              <Box sx={{ fontSize: 13, color: '#6B7280', py: 1 }}>No majors found.</Box>
            )}
            {searchMatches.map((m) => (
              <MajorRowItem key={m.major_name} m={m} />
            ))}
          </Box>
        ) : (
          <Box sx={{ maxHeight: '50vh', overflow: 'auto' }}>
            {grouped.map(([category, majors]) => {
              const isOpen = expanded.includes(category);
              return (
                <Box key={category}>
                  <button
                    type="button"
                    onClick={() => setExpanded((p) => toggle(p, category))}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 8,
                      padding: '8px 6px',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      fontFamily: "'DM Sans', sans-serif",
                    }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <Chevron open={isOpen} />
                      <span style={{ fontWeight: 600, fontSize: 14 }}>{category}</span>
                    </span>
                    <span style={{ fontSize: 12, color: '#6B7280' }}>{majors.length}</span>
                  </button>
                  {isOpen && (
                    <Box sx={{ pl: 3, pb: 1 }}>
                      {majors.map((m) => (
                        <MajorRowItem key={m.major_name} m={m} />
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions className="p-4">
        <Button
          variant="contained"
          onClick={() => {
            formik.setFieldValue('major_names', selectedMajors);
            formik.handleSubmit();
            setOpen(false);
          }}
        >
          Show Colleges
        </Button>
      </DialogActions>
    </Dialog>
  );
}
